// src/screens/PdfPreview.js
import { getAuthorSignature, formatSignatureLines } from '../utils/signatureUtils.js';
import { getCountryFromIP } from '../utils/geoUtils.js';

const SEV_COLORS = {
  low:      { color: '#3B6D11', bg: '#EAF3DE' },
  medium:   { color: '#854F0B', bg: '#FAEEDA' },
  high:     { color: '#993C1D', bg: '#FAECE7' },
  critical: { color: '#A32D2D', bg: '#FCEBEB' },
};

const URG_LABELS = {
  deferred:  'Diferida · >12 meses',
  planned:   'Planificada · 3–12 meses',
  immediate: 'Inmediata · <3 meses',
  emergency: 'Emergencia · <48 horas',
};

export function PdfPreviewScreen(container, state, actions) {
  const d = state.diagnosis;
  if (!d) { actions.navigate('result'); return; }

  const sv   = SEV_COLORS[d.severity] || SEV_COLORS.medium;
  const uLbl = URG_LABELS[d.urgency]  || URG_LABELS.planned;

  // Steps normalised
  const steps = Array.isArray(d.repair?.procedure?.es)
    ? d.repair.procedure.es
    : Array.isArray(d.repair_steps_es) ? d.repair_steps_es : [];

  const premium = d.materials?.premium?.product || d.premium_product || '—';
  const premNote = d.materials?.premium?.note?.es || d.premium_note_es || '';
  const generic  = d.materials?.generic?.es || d.generic_material_es || '—';
  const normRef  = d.repair?.norm_reference || d.norm_reference || '—';

  const diagId   = 'CPR-' + Date.now().toString().slice(-6);
  const dateStr  = new Date().toLocaleDateString('es-MX', { dateStyle: 'long' });

  // Signature — load async, default to Mexico for preview
  const sig = getAuthorSignature('MX');
  const sigLines = formatSignatureLines(sig);

  container.innerHTML = `
    <div class="screen">
      <div class="top-bar">
        <div class="logo">
          <div class="logo-sq"><i class="ti ti-building-factory-2" aria-hidden="true"></i></div>
          <div><div class="logo-name">CONCREPRO</div></div>
        </div>
      </div>
      <div class="screen-body">
        <button class="btn-back" id="btn-back">
          <i class="ti ti-arrow-left" aria-hidden="true"></i> Resultado
        </button>
        <h2 style="font-size:20px;font-weight:500;margin-bottom:14px">Vista previa del reporte</h2>

        <!-- PDF DOCUMENT -->
        <div class="pdf-preview">

          <!-- Header -->
          <div class="pdf-header">
            <div>
              <div style="font-size:15px;font-weight:500;color:#F6A623;font-family:'Outfit',sans-serif;letter-spacing:.05em">
                CONCREPRO
              </div>
              <div style="font-size:9px;color:#8080a0;margin-top:2px;font-family:'JetBrains Mono',monospace">
                Reporte de Diagnóstico · ${dateStr}
              </div>
            </div>
            <div style="text-align:right">
              <div style="font-size:11px;color:#F6A623;font-family:'JetBrains Mono',monospace">⭐ Sika México</div>
              <div style="font-size:9px;color:#8080a0;font-family:'JetBrains Mono',monospace">sika.com.mx</div>
            </div>
          </div>

          <div class="pdf-body">
            <!-- S01: Datos -->
            <div class="pdf-section">
              <div class="pdf-section-heading">01 — Datos del diagnóstico</div>
              ${[
                ['ID Reporte',  diagId],
                ['Patología',   `<strong>${d.pathology_name?.es || d.pathology_name_es || '—'}</strong>`],
                ['Norma',       d.norm || '—'],
                ['Confianza',   `<span style="font-family:'JetBrains Mono',monospace">${d.confidence || 0}%</span>`],
                ['Herramienta', 'ConcrePro v1.0 · IA'],
              ].map(([k, v]) => `
                <div class="pdf-row">
                  <span class="pdf-key">${k}</span>
                  <span style="font-size:11px;color:#111827">${v}</span>
                </div>`).join('')}
              <div style="margin-top:8px">
                <span class="pdf-badge" style="background:${sv.bg};color:${sv.color}">
                  ■ ${d.severity === 'low' ? 'Baja' : d.severity === 'medium' ? 'Media' : d.severity === 'high' ? 'Alta' : 'Crítica'}
                </span>
                <span class="pdf-badge" style="background:${sv.bg};color:${sv.color}">● ${uLbl}</span>
              </div>
            </div>

            <!-- S02: Foto -->
            ${state.image ? `
              <div class="pdf-section">
                <div class="pdf-section-heading">02 — Fotografía del defecto</div>
                <img src="${state.image.dataUrl}" alt="Área analizada"
                     style="width:100%;max-height:170px;object-fit:cover;border-radius:4px;display:block">
                <div style="font-size:9px;color:#6b7280;margin-top:4px">Figura 1. Área analizada.</div>
              </div>` : ''}

            <!-- S03: Causa raíz -->
            <div class="pdf-section">
              <div class="pdf-section-heading">03 — Causa raíz</div>
              <div style="font-size:11px;color:#374151;line-height:1.55">
                ${d.root_cause?.es || d.root_cause_es || '—'}
              </div>
            </div>

            <!-- S04: Procedimiento -->
            <div class="pdf-section">
              <div class="pdf-section-heading">04 — Procedimiento de reparación</div>
              <div style="font-size:9px;color:#9ca3af;margin-bottom:8px;font-family:'JetBrains Mono',monospace">
                ${normRef}
              </div>
              ${steps.map((step, i) => `
                <div style="display:flex;gap:7px;font-size:11px;color:#374151;margin-bottom:5px;line-height:1.4">
                  <span style="font-family:'JetBrains Mono',monospace;color:#F6A623;font-size:9px;min-width:18px;flex-shrink:0;padding-top:1px">
                    0${i + 1}
                  </span>
                  <span>${step}</span>
                </div>`).join('')}
            </div>

            <!-- S05: Materiales -->
            <div class="pdf-section">
              <div class="pdf-section-heading">05 — Materiales recomendados</div>
              <div class="pdf-sika-box">
                <div style="font-size:10px;color:#d97706;margin-bottom:4px">
                  ⭐ Solución técnica recomendada — Sika México
                </div>
                <div style="font-size:12px;font-weight:500;color:#111827">${premium}</div>
                ${premNote ? `<div style="font-size:10px;color:#d97706;margin-top:3px">${premNote}</div>` : ''}
                <div style="font-size:9px;color:#d97706;margin-top:5px;font-family:'JetBrains Mono',monospace">
                  www.sika.com.mx
                </div>
              </div>
              <div style="font-size:10px;color:#374151;margin-top:6px">
                <strong>Alternativa genérica:</strong> ${generic}
              </div>
            </div>

            <!-- S06: Normas -->
            <div class="pdf-section">
              <div class="pdf-section-heading">06 — Respaldo normativo</div>
              <div style="font-size:10px;color:#374151;line-height:1.6">
                ACI · ICRI · EN 1504 (series 1–10) · RILEM TC · fib Model Code 2010
              </div>
            </div>

            <!-- Disclaimer -->
            <div class="pdf-disclaimer">
              ⚠ DISCLAIMER PROFESIONAL: Este reporte es generado por ConcrePro, herramienta de diagnóstico
              asistido por inteligencia artificial. Su objetivo es orientar al profesional; NO sustituye la
              inspección presencial de un perito o ingeniero certificado. Cualquier intervención estructural
              debe ser validada por profesional habilitado.
            </div>
          </div>

          <!-- Footer + Signature -->
          <div class="pdf-footer">
            <div style="font-size:9px;color:#6b7280;font-family:'JetBrains Mono',monospace">
              ConcrePro v1.0 × Sika México<br>concrepro.app
            </div>
            <div style="text-align:right">
              ${sigLines.map((line, i) => `
                <div style="font-size:${i === 0 ? '12px' : '9px'};${i === 0 ? 'font-weight:500;' : ''}color:${i === 0 ? '#111827' : '#6b7280'};font-family:${i > 0 ? "'JetBrains Mono',monospace" : 'inherit'}">
                  ${line}
                </div>`).join('')}
            </div>
          </div>
        </div>
        <!-- END PDF DOCUMENT -->

        <button id="btn-download" class="btn-secondary" style="margin-top:12px;cursor:default;opacity:.6" disabled>
          <i class="ti ti-download" aria-hidden="true"></i> Descargar PDF (próxima versión)
        </button>
        <button class="btn-secondary" id="btn-back-2">
          <i class="ti ti-arrow-left" aria-hidden="true"></i> Volver al diagnóstico
        </button>
      </div>
    </div>`;

  container.querySelector('#btn-back')?.addEventListener('click', () => actions.navigate('result'));
  container.querySelector('#btn-back-2')?.addEventListener('click', () => actions.navigate('result'));

  // Update signature asynchronously using real IP geolocation
  getCountryFromIP().then(country => {
    const realSig = getAuthorSignature(country);
    const realLines = formatSignatureLines(realSig);
    const sigEl = container.querySelector('.pdf-footer > div:last-child');
    if (sigEl) {
      sigEl.innerHTML = realLines.map((line, i) => `
        <div style="font-size:${i === 0 ? '12px' : '9px'};${i === 0 ? 'font-weight:500;' : ''}color:${i === 0 ? '#111827' : '#6b7280'};font-family:${i > 0 ? "'JetBrains Mono',monospace" : 'inherit'}">
          ${line}
        </div>`).join('');
    }
  }).catch(() => {/* keep default signature */});
}
